/**
 * Compare two workflow versions' DAG JSON side-by-side using Monaco's
 * built-in DiffEditor (already wired by the canonical schema viewer).
 *
 * Opened from the editor topbar's "Versions" item — pick two versions,
 * see what changed. Pure read-only; no apply / rollback (a rollback is
 * just "save the older version's definition as a new version", which
 * the user can do via Clone + edit if needed).
 */
import { useQueries } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import * as React from "react";
import { DiffEditor } from "@monaco-editor/react";

import { Alert } from "@/components/ui/alert";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useTheme } from "@/theme/ThemeProvider";
import {
  workflowsApi,
  type WorkflowVersionSummary,
} from "@/lib/api-workflows";

export interface VersionDiffDialogProps {
  workflowId: string;
  versions: WorkflowVersionSummary[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VersionDiffDialog({
  workflowId,
  versions,
  open,
  onOpenChange,
}: VersionDiffDialogProps) {
  const { resolved } = useTheme();

  // Default: compare the two most recent versions.
  const sorted = React.useMemo(
    () => [...versions].sort((a, b) => b.version - a.version),
    [versions],
  );
  const [leftId, setLeftId] = React.useState<string>("");
  const [rightId, setRightId] = React.useState<string>("");

  React.useEffect(() => {
    if (open && sorted.length >= 2 && !leftId && !rightId) {
      setRightId(sorted[0].id);
      setLeftId(sorted[1].id);
    }
  }, [open, sorted, leftId, rightId]);

  const results = useQueries({
    queries: [
      {
        queryKey: ["workflow-version", workflowId, leftId],
        queryFn: () => workflowsApi.getVersion(workflowId, leftId),
        enabled: open && !!leftId,
      },
      {
        queryKey: ["workflow-version", workflowId, rightId],
        queryFn: () => workflowsApi.getVersion(workflowId, rightId),
        enabled: open && !!rightId,
      },
    ],
  });
  const [leftQ, rightQ] = results;
  const leftJson = leftQ.data
    ? JSON.stringify(leftQ.data.definition, null, 2)
    : "";
  const rightJson = rightQ.data
    ? JSON.stringify(rightQ.data.definition, null, 2)
    : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-5xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Compare versions</DialogTitle>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {sorted.length < 2 ? (
            <Alert tone="info">
              You need at least two versions to compare. Save a new
              version and come back.
            </Alert>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Left (older)
                  </Label>
                  <select
                    className="block w-full border border-border bg-background px-2 py-1.5 text-xs"
                    value={leftId}
                    onChange={(e) => setLeftId(e.target.value)}
                  >
                    {sorted.map((v) => (
                      <option key={v.id} value={v.id}>
                        v{v.version} —{" "}
                        {new Date(v.created_at).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Right (newer)
                  </Label>
                  <select
                    className="block w-full border border-border bg-background px-2 py-1.5 text-xs"
                    value={rightId}
                    onChange={(e) => setRightId(e.target.value)}
                  >
                    {sorted.map((v) => (
                      <option key={v.id} value={v.id}>
                        v{v.version} —{" "}
                        {new Date(v.created_at).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="h-[60vh] rounded-md border border-border/60">
                {leftQ.isLoading || rightQ.isLoading ? (
                  <div className="flex h-full items-center justify-center text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                ) : (
                  <DiffEditor
                    original={leftJson}
                    modified={rightJson}
                    language="json"
                    theme={resolved === "dark" ? "vs-dark" : "vs"}
                    options={{
                      readOnly: true,
                      renderSideBySide: true,
                      minimap: { enabled: false },
                      lineNumbers: "on",
                      scrollBeyondLastLine: false,
                    }}
                  />
                )}
              </div>
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
